import React, { useEffect, useState } from "react";
import ProfileLayout from "../../layout/ProfileLayout";
import { historyService } from "../../services";
import { Empty, Pagination, Panel, PanelHead, StatusTag, money } from "../../components/ui";

// ประวัติทุกหน้าอ่านจาก v1/market/orders (กรองด้วย process_type=lucid_topup + order_type)
const detailOf = (row, key) => row.detail?.[key] ?? "-";
const inputsOf = (row) => {
  const inputs = row.detail?.inputs;
  if (!inputs) return "-";
  const values = Object.values(inputs).filter(Boolean);
  return values.length ? values.join(" / ") : "-";
};

export const HISTORY_CONFIG = {
  credit: {
    title: "⇄ ประวัติการเติมเครดิต",
    empty: "ยังไม่มีข้อมูลประวัติการเติมเครดิต",
    fetch: historyService.credit,
    columns: [
      { key: "index", label: "#" },
      { key: "code", label: "เลขที่คำสั่งซื้อ" },
      { key: "refNo", label: "Ref" },
      { key: "amount", label: "จำนวนเงิน", render: (row) => `฿ ${money(row.amount)}` },
      { key: "createdAt", label: "วัน-เวลา" },
      { key: "status", label: "สถานะ", render: (row) => <StatusTag status={row.status} /> },
    ],
  },
  game: {
    title: "📦 ประวัติการเติมเกม",
    empty: "ยังไม่มีข้อมูลประวัติการเติมเกม",
    fetch: historyService.game,
    columns: [
      { key: "index", label: "#" },
      { key: "code", label: "เลขที่คำสั่งซื้อ" },
      { key: "game", label: "ชื่อเกม", render: (row) => detailOf(row, "itemName") },
      { key: "package", label: "แพ็ก", render: (row) => detailOf(row, "packageName") },
      { key: "player", label: "ข้อมูลผู้เล่น", render: inputsOf },
      { key: "quantity", label: "จำนวน" },
      { key: "amount", label: "ราคาสุทธิ", render: (row) => `฿ ${money(row.amount)}` },
      { key: "createdAt", label: "วัน-เวลา" },
      { key: "status", label: "สถานะ", render: (row) => <StatusTag status={row.status} /> },
    ],
  },
  mobile: {
    title: "📦 ประวัติการเติมเงินมือถือ",
    empty: "ยังไม่มีข้อมูลประวัติการเติมเงินมือถือ",
    fetch: historyService.mobile,
    columns: [
      { key: "index", label: "#" },
      { key: "code", label: "เลขที่คำสั่งซื้อ" },
      { key: "operator", label: "เครือข่าย", render: (row) => detailOf(row, "itemName") },
      { key: "phone", label: "เบอร์โทรศัพท์", render: inputsOf },
      { key: "amount", label: "จำนวนเงิน", render: (row) => `฿ ${money(row.amount)}` },
      { key: "createdAt", label: "วัน-เวลา" },
      { key: "status", label: "สถานะ", render: (row) => <StatusTag status={row.status} /> },
    ],
  },
  card: {
    title: "📦 ประวัติสั่งซื้อบัตรเติมเงิน",
    empty: "ยังไม่มีข้อมูลประวัติสั่งซื้อบัตรเติมเงิน",
    fetch: historyService.card,
    columns: [
      { key: "index", label: "#" },
      { key: "code", label: "เลขที่คำสั่งซื้อ" },
      { key: "cardName", label: "ชื่อบัตร", render: (row) => detailOf(row, "itemName") },
      { key: "package", label: "มูลค่า", render: (row) => detailOf(row, "packageName") },
      { key: "amount", label: "ราคาสุทธิ", render: (row) => `฿ ${money(row.amount)}` },
      { key: "createdAt", label: "วัน-เวลา" },
      { key: "status", label: "สถานะ", render: (row) => <StatusTag status={row.status} /> },
    ],
  },
  product: {
    title: "📦 ประวัติสั่งซื้อสินค้า",
    empty: "ยังไม่มีข้อมูลประวัติสั่งซื้อสินค้า",
    fetch: historyService.product,
    columns: [
      { key: "index", label: "#" },
      { key: "code", label: "เลขที่คำสั่งซื้อ" },
      { key: "productName", label: "สินค้า" },
      { key: "quantity", label: "จำนวน" },
      { key: "amount", label: "ราคาสุทธิ", render: (row) => `฿ ${money(row.amount)}` },
      { key: "orderStatus", label: "การจัดส่ง" },
      { key: "createdAt", label: "วัน-เวลา" },
      { key: "status", label: "สถานะ", render: (row) => <StatusTag status={row.status} /> },
    ],
  },
};

export default function HistoryPage({ type }) {
  const config = HISTORY_CONFIG[type];
  const [data, setData] = useState({ rows: [], total: 0 });
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    config
      .fetch({ page, perPage })
      .then((result) => {
        if (!alive) return;
        setData(result);
        setLoading(false);
      })
      .catch(() => {
        if (!alive) return;
        setData({ rows: [], total: 0 });
        setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [config, page, perPage, type]);

  return (
    <ProfileLayout>
      <Panel>
        <PanelHead title={config.title} />

        <div className="lt-table-wrap">
          <table className="lt-table">
            <thead>
              <tr>
                {config.columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {!loading && data.rows.length > 0
                ? data.rows.map((row, index) => (
                    <tr key={row.id}>
                      {config.columns.map((column) => (
                        <td key={column.key}>
                          {column.key === "index"
                            ? (page - 1) * perPage + index + 1
                            : column.render
                            ? column.render(row)
                            : row[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))
                : null}
            </tbody>
          </table>
        </div>

        {loading ? <Empty text="กำลังโหลดข้อมูล..." /> : data.rows.length === 0 ? <Empty text={config.empty} /> : null}

        <Pagination
          page={page}
          perPage={perPage}
          total={data.total || 0}
          onPageChange={setPage}
          onPerPageChange={(value) => {
            setPerPage(value);
            setPage(1);
          }}
        />
      </Panel>
    </ProfileLayout>
  );
}
